import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  SquaresFour, GridFour, DotsThree, Lightning, Barcode, SignOut, ShoppingCart, CalendarBlank,
} from "@phosphor-icons/react";
import DeliveryTimeSlider from "@/components/pos/DeliveryTimeSlider";
import FullscreenToggle from "@/components/FullscreenToggle";

/**
 * POS top bar. Handles both mobile (compact) and desktop (full) layouts
 * from a single component driven by ``serverMobile``. Extracted from
 * POSPage.jsx (~120 lines inline). Pure JSX — all state/actions stay
 * on the parent.
 */
const PosTopBar = ({
  serverMobile,
  showSidebar,
  user,
  view,
  ordersSubView,
  moreSubView,
  ticketOpen,
  selectedTable,
  orderItems,
  setView,
  setTicketOpen,
  handleStartTicket,
  handleQuickOrder,
  setShowBarcodeScan,
  setShowReservations,
  logout,
  navigate,
}) => {
  if (serverMobile) {
    return (
      <div className="mb-3 space-y-2" data-testid="mobile-top-bar">
        <div className="flex items-center justify-between bg-white rounded-xl px-3 py-2.5 border border-border shadow-sm">
          <div className="flex items-center gap-2 min-w-0">
            <span className="font-heading font-medium text-sm truncate">{user?.name}</span>
            {selectedTable && <Badge variant="outline" className="font-mono text-xs shrink-0">{selectedTable.name}</Badge>}
            {!selectedTable && ticketOpen && orderItems.length > 0 && (
              <Badge variant="outline" className="text-primary border-primary/30 text-xs shrink-0">Ticket</Badge>
            )}
          </div>
          <div className="flex items-center gap-1.5 shrink-0">
            {orderItems.length > 0 && !ticketOpen && (
              <Button size="sm" onClick={() => setTicketOpen(true)} className="gap-1 bg-primary relative" data-testid="mobile-cart-btn">
                <ShoppingCart size={16} weight="fill" />
                <span className="absolute -top-1.5 -right-1.5 bg-accent text-white text-[10px] font-bold w-5 h-5 rounded-full flex items-center justify-center">{orderItems.length}</span>
              </Button>
            )}
            <Button
              variant="outline" size="sm"
              onClick={() => setShowReservations && setShowReservations(true)}
              className="gap-1 px-2"
              data-testid="mobile-reservations-btn"
              title="Reservations"
            >
              <CalendarBlank size={16} weight="bold" />
            </Button>
            <FullscreenToggle className="px-2" />
            <Button variant="outline" size="sm" onClick={() => { logout(); navigate("/login"); }} className="gap-1 text-destructive border-destructive/30 px-2" data-testid="server-logout-btn">
              <SignOut size={16} weight="bold" />
            </Button>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <div className="flex bg-secondary rounded-lg p-1 flex-1" data-testid="view-toggle">
            <button
              onClick={() => { setView("floor"); setTicketOpen(false); }}
              className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-2.5 rounded-md text-sm font-medium transition-colors ${view === "floor" ? "bg-white shadow-sm text-foreground" : "text-muted-foreground"}`}
              data-testid="view-floor-btn"
            >
              <SquaresFour size={16} /> Tables
            </button>
            <button
              onClick={handleStartTicket}
              className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-2.5 rounded-md text-sm font-medium transition-colors ${view === "menu" ? "bg-white shadow-sm text-foreground" : "text-muted-foreground"}`}
              data-testid="view-menu-btn"
            >
              <GridFour size={16} /> Orders
            </button>
            <button
              onClick={() => setView("more")}
              className={`flex-1 flex items-center justify-center gap-1.5 px-2 py-2.5 rounded-md text-xs font-medium transition-colors ${view === "more" ? "bg-white shadow-sm text-foreground" : "text-muted-foreground"}`}
              data-testid="view-more-btn"
            >
              <DotsThree size={16} weight="bold" />
            </button>
          </div>
          <Button size="sm" onClick={handleQuickOrder} className="gap-1 bg-accent hover:bg-accent/90 px-3 py-2.5 h-auto" data-testid="quick-order-btn">
            <Lightning size={16} weight="fill" />
          </Button>
        </div>
      </div>
    );
  }

  // Desktop
  const title =
    view === "floor" ? "Tables" :
    view === "more" ? (moreSubView === "tips" ? "My Tips" : "Customers") :
    view === "menu" ? (
      ordersSubView === "menu" ? "Orders" :
      ordersSubView === "open" ? "Open Orders" :
      ordersSubView === "online" ? "Online Orders" : "Closed Orders"
    ) : "Orders";

  return (
    <div className="flex items-center justify-between mb-4 bg-white rounded-xl px-5 py-3 border border-border shadow-sm">
      <div className="flex items-center gap-4">
        {!showSidebar && (
          <div className="flex items-center gap-2 pr-3 mr-3 border-r border-border">
            <span className="font-heading font-medium text-sm">{user?.name}</span>
          </div>
        )}
        <h1 className="font-heading text-xl font-medium text-foreground">{title}</h1>
        {selectedTable && (
          <Badge variant="outline" className="font-mono">{selectedTable.name}</Badge>
        )}
        {!selectedTable && ticketOpen && orderItems.length > 0 && (
          <Badge variant="outline" className="text-primary border-primary/30">New ticket</Badge>
        )}
      </div>
      <div className="flex items-center gap-3">
        <DeliveryTimeSlider user={user} />
        <div className="flex bg-secondary rounded-lg p-1" data-testid="view-toggle">
          <button
            onClick={() => { setView("floor"); setTicketOpen(false); }}
            className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-colors ${view === "floor" ? "bg-white shadow-sm text-foreground" : "text-muted-foreground hover:text-foreground"}`}
            data-testid="view-floor-btn"
          >
            <SquaresFour size={16} /> Tables
          </button>
          <button
            onClick={handleStartTicket}
            className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-colors ${view === "menu" ? "bg-white shadow-sm text-foreground" : "text-muted-foreground hover:text-foreground"}`}
            data-testid="view-menu-btn"
          >
            <GridFour size={16} /> Orders
          </button>
          <button
            onClick={() => setView("more")}
            className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-colors ${view === "more" ? "bg-white shadow-sm text-foreground" : "text-muted-foreground hover:text-foreground"}`}
            data-testid="view-more-btn"
          >
            <DotsThree size={16} weight="bold" /> More
          </button>
        </div>
        <Button variant="default" size="sm" onClick={handleQuickOrder} className="gap-1 bg-accent hover:bg-accent/90" data-testid="quick-order-btn">
          <Lightning size={16} weight="fill" /> Quick Order
        </Button>
        <Button
          variant="outline" size="sm"
          onClick={() => setShowBarcodeScan(true)}
          className="gap-1"
          data-testid="scan-barcode-btn"
          title="Scan a barcode to add an item"
        >
          <Barcode size={16} weight="bold" /> Scan
        </Button>
        <Button
          variant="outline" size="sm"
          onClick={() => setShowReservations && setShowReservations(true)}
          className="gap-1"
          data-testid="reservations-btn"
          title="See today's reservations and seat a party"
        >
          <CalendarBlank size={16} weight="bold" /> Reservations
        </Button>
        <FullscreenToggle />
        {!showSidebar && (
          <Button variant="outline" size="sm" onClick={() => { logout(); navigate("/login"); }} className="gap-1 text-destructive border-destructive/30 hover:bg-destructive/10" data-testid="server-logout-btn">
            <SignOut size={16} weight="bold" /> Out
          </Button>
        )}
      </div>
    </div>
  );
};

export default PosTopBar;
